import { IconEdit } from "@tabler/icons-react";
import { Descriptions, Select, Typography, theme } from "antd";
import type React from "react";
import { type ReactNode, useState } from "react";
import { toLabel } from "@/common/utils";

/**
 * Editable text field: antd's always-visible edit-pen trigger.
 * Omitting `onChange` renders plain read-only text.
 */
export function EditableText({
	onChange,
	paragraph,
	style,
	editableExtra,
	children,
}: {
	onChange?: (value: string) => void;
	/** Render as Typography.Paragraph (multi-line) instead of Text. */
	paragraph?: boolean;
	style?: React.CSSProperties;
	/** Extra antd `editable` config, e.g. `{ autoSize: { minRows: 3 } }`. */
	editableExtra?: Record<string, unknown>;
	children: ReactNode;
}) {
	const Comp = paragraph ? Typography.Paragraph : Typography.Text;
	return (
		<Comp
			style={{ display: "block", width: "100%", ...style }}
			editable={
				onChange
					? {
							// antd fires onChange on every edit end — skip no-op saves.
							onChange: (value: string) => {
								if (value !== String(children ?? "")) onChange(value);
							},
							triggerType: ["text", "icon"],
							icon: <IconEdit size={14} stroke={1.8} />,
							...editableExtra,
						}
					: false
			}
		>
			{children}
		</Comp>
	);
}

/**
 * Inline field with an always-visible edit-pen — for fields whose edit opens a
 * drawer or swaps in a control. Only the pen is interactive, so children may
 * contain their own links. Uses the same icon as {@link EditableText}, so every
 * editable details row reads the same.
 */
export function EditableClickable({
	onClick,
	children,
}: {
	onClick: () => void;
	children: ReactNode;
}) {
	const { token } = theme.useToken();
	return (
		<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
			{children}
			<button
				type="button"
				aria-label="Edit"
				onClick={onClick}
				style={{
					all: "unset",
					cursor: "pointer",
					display: "inline-flex",
					// gray like the copy/text-edit icons, so the value stays the focus
					color: token.colorTextSecondary,
				}}
			>
				<IconEdit size={14} stroke={1.8} />
			</button>
		</span>
	);
}

/** Click the read-mode child to swap in a Select; picking a value saves and closes. */
export function InlineSelect<T extends string>({
	value,
	options,
	onChange,
	children,
}: {
	value: T;
	options: { value: T; label: string }[];
	onChange?: (value: T) => void;
	children: ReactNode;
}) {
	const [editing, setEditing] = useState(false);
	if (!onChange) return <>{children}</>;
	if (!editing)
		return (
			<EditableClickable onClick={() => setEditing(true)}>
				{children}
			</EditableClickable>
		);
	return (
		<Select<T>
			size="small"
			autoFocus
			defaultOpen
			value={value}
			options={options}
			style={{ minWidth: 170 }}
			onChange={(v) => {
				if (v !== value) onChange(v);
				setEditing(false);
			}}
			onBlur={() => setEditing(false)}
		/>
	);
}

/** Enum label map → antd Select options. */
export const toOptions = <T extends string>(labels: Record<T, string>) =>
	(Object.entries(labels) as [T, string][]).map(([value, label]) => ({
		value,
		label,
	}));

export type BaseDetailsField<T extends object> = {
	[K in keyof T]-?: {
		key: K;
		/** Row label. Falls back to `tooltip`, then the key name. */
		label?: string;
		/** Legacy hover text; used as the label when `label` is unset. */
		tooltip?: string;
		/** ponytail: icon is retained for back-compat with older configs but no longer rendered. */
		icon?: React.ComponentType<any>;
		render?: (value: T[K], item: T) => React.ReactNode;
		hidden?: boolean | ((value: T[K], item: T) => boolean);
	};
}[keyof T];

export interface BaseDetailsProps<T extends object> {
	item: T;
	fields: BaseDetailsField<T>[];
	size?: "small" | "middle" | "large";
	/** Width of the label column in px. */
	labelWidth?: number;
}

function renderDefaultDetailsValue(value: unknown): React.ReactNode {
	if (value === null || value === undefined || value === "")
		return toLabel(null);
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (typeof value === "string" || typeof value === "number") return value;
	if (value instanceof Date) return value.toLocaleString();
	return JSON.stringify(value);
}

/**
 * Render a single object's properties as dense, label-led rows with a divider between each.
 * Each field controls its own editing via its render function (e.g. Typography editable, InlineSelect).
 */
export function BaseDetails<T extends object>({
	item,
	fields,
	size = "small",
	labelWidth = 150,
}: BaseDetailsProps<T>) {
	const { token } = theme.useToken();
	const cellStyle = {
		paddingTop: 0,
		paddingBottom: 3,
		borderBottom: `1px solid ${token.colorSplit}`,
	};

	const visible = fields.filter((field) =>
		typeof field.hidden === "function"
			? !field.hidden(item[field.key], item)
			: !field.hidden,
	);

	return (
		<Descriptions
			column={1}
			size={size}
			colon={false}
			styles={{ label: { width: labelWidth }, content: cellStyle }}
		>
			{visible.map((field) => {
				const value = item[field.key];
				return (
					<Descriptions.Item
						key={String(field.key)}
						label={field.label ?? field.tooltip ?? String(field.key)}
					>
						{field.render
							? field.render(value, item)
							: renderDefaultDetailsValue(value)}
					</Descriptions.Item>
				);
			})}
		</Descriptions>
	);
}
